import { saveMarkdown } from '../../../../services/fileSystemService'

/** A judge rubric — free-form markdown stored in the eval `rubric` entity. */
export type Rubric = {
  rubric_id: string
  name: string
  version_id: string
  body: string
  created_at: string
  version_created_at: string
}

export type RubricListResponse = {
  rubrics: Rubric[]
  total: number
  limit: number
  offset: number
}

/** Page size for the server-side paginated rubric list (backend caps limit at 50). */
export const RUBRICS_PAGE_SIZE = 25

export type RubricFilters = {
  /** Case-insensitive name search; sent to the backend as `q`. */
  q?: string
  page: number
  pageSize: number
}

export const getDefaultRubricFilters = (): RubricFilters => ({
  page: 1,
  pageSize: RUBRICS_PAGE_SIZE,
})

/** Build the query string for the list endpoint: name search + pagination. */
export const buildRubricsListQuery = (filters: RubricFilters): URLSearchParams => {
  const query = new URLSearchParams()
  const q = filters.q?.trim()
  if (q) {
    query.set('q', q)
  }
  query.set('limit', String(filters.pageSize))
  query.set('offset', String((filters.page - 1) * filters.pageSize))
  return query
}

export type RubricCreateInput = {
  name: string
  body: string
}

export type RubricUpdateInput = {
  rubric_id: string
  body: string
}

/**
 * Pre-flight the backend's create/update rules: name and body must both be
 * non-empty. Returns an error message, or null if valid.
 */
export const validateRubric = (name: string, body: string): string | null => {
  if (!name.trim()) {
    return 'Name is required.'
  }
  if (!body.trim()) {
    return 'Body is required.'
  }
  return null
}

/**
 * Build the PUT payload for editing a rubric. The backend update is body-only —
 * the name is immutable and updating appends a new version.
 */
export const buildRubricUpdateInput = (
  rubric: Rubric,
  body: string,
): RubricUpdateInput => ({
  rubric_id: rubric.rubric_id,
  body,
})

/** Build a safe download filename from the rubric name. */
export const rubricFilename = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `${slug || 'rubric'}.md`
}

/** Download a rubric's markdown body as a file named after the rubric. */
export const downloadRubricMarkdown = (rubric: Rubric): void => {
  saveMarkdown(rubric.body, rubricFilename(rubric.name))
}
